import { VisionVO } from '@wizeoo/akm-compute-lib';
import { WIZ_UPLOAD_ACCEPT_FORMAT, WizStringUtils, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizPopUp, WizUploadManager, WizCommandManager, WizIcon, UploadParameter, ImageUploadParameter, WizEventManager, WizEvent, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { Attachment } from '@wizeoo/akm-message-lib';
import { BatchCmd } from '../../command/batch/batch.cmd';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { AttachmentService } from '../../command/core/attachment/attachment.service';
import { AttachmentCmd } from '../../command/core/attachment/attachment.cmd';

/** constantes de batchs pour l'import */
export enum BATCH_KIND {
  FREEDATA = 'FREEDATA',
  FREEDATAARGS = "FREEDATAARGS",
  FREEDATAVALUE = 'FREEDATAVALUE',
  BU = 'BUSINESSUNIT',
  CHRONOCODE = 'CHRONOCODE',
  VISION = 'VISION',
  META_VISION = 'MVISION',
  MEASURE = 'MEASURE',
  LANGUAGE = 'LANGUAGE',
  COUNTRY = 'COUNTRY',
  ROLE = 'ROLE',
  USER = 'USER',
  TEMPLATING = 'TEMPLATING',
  TABS = 'TABS',
  STYLE = 'STYLE',
  FORMAT = 'DATAFORMAT',
  CSSRENDER = 'CSSRENDER', 
  WORKFLOW = 'WORKFLOW',
  PROFILE = 'PROFILE',
  BASELINE = 'BASELINE',
  WG = 'WG',
  INCEXC = 'INCEXC',
  SCENARIO = 'SCENARIO',
  META_BU = 'MBU',
  MATRIX = 'MATRIX',
}

/**
 * Classe générique des appels de batchs
 */
export class BatchEngine {
  
  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }
  
  
  /** liste des fichiers chargés */
  public files: File[] = [];
  // type de batch en attente d'import
  private currentImportKind: string; 

  /**
   * demande d'export depuis un multidisplay
   * @param kind string, ExternalAPI.BATCH_xxx
   */
  public export(kind: string, fileFormat: string = null, criteriaKey: string = null): void {
    new BatchCmd(this.model).export(kind, fileFormat, criteriaKey).execute();
  }

  /**
   * Récupère le fichier utilisateur
   */
  public import(kind: string): void {
    this.currentImportKind = kind;

    let params: UploadParameter = {
      callback: this.uploadCallback.bind(this),
      accept: [
        WIZ_UPLOAD_ACCEPT_FORMAT.FILE_CSV,
        WIZ_UPLOAD_ACCEPT_FORMAT.FILE_TXT,
        WIZ_UPLOAD_ACCEPT_FORMAT.FILE_JSON,
        WIZ_UPLOAD_ACCEPT_FORMAT.FILE_ZIP,
        WIZ_UPLOAD_ACCEPT_FORMAT.FILE_XLS
      ],
      multiple: false, // false par défaut
      maxSize: 20 // 30 Mo
    }

    WizUploadManager.loadFile(params);
  }

  /**
   * Callback de l'import
   * @param parameters 
   */
  public uploadCallback(parameters: UploadParameter) {
    if (parameters.errors?.length > 0) {
      parameters.errors.forEach(err => {
        WizPopUp.toast(err, WizIcon.TOAST_ERROR).show();
      });
    } else {
      let file: File = parameters.files[0];
      let ext: string = ''; // l'extension du fichier pour détermination du type
      let isZip: boolean = false; // pas de contrôle sur les zip (pour l'instant, TODO)

      if (!!file.name) {
        ext = file.name.slice((file.name.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
      }

      if (WIZ_UPLOAD_ACCEPT_FORMAT.FILE_ZIP.indexOf(ext) > -1) { // c'est un zip... ou pas
        isZip = true;
      }

      if (isZip) { // pas de contrôle sur les zips
        new BatchCmd(this._model).import(this.currentImportKind, parameters.files[0].name, parameters.contents[0]).execute();
      } else { // on va contrôles la première ligne du fichier
        var reader = new FileReader();
        let currentImportKind = this.currentImportKind;
        let model: Model = this.model;
        reader.onload = function (e) {
          let text: string = reader.result as string; // the entire file
          let realKind: string = WizStringUtils.eraseNR(text.split('\n').shift()); // first line 
          // console.log('realKind: ',realKind); 

          let finalKind: string = currentImportKind;
          // LMA patch Codif pour intégration FREEDATA / ARGS sur le même import
          if (currentImportKind === BATCH_KIND.FREEDATA) {
            if (realKind === BATCH_KIND.FREEDATAARGS) {
              finalKind = BATCH_KIND.FREEDATAARGS;
            }
          }
          new BatchCmd(model).import(finalKind, parameters.files[0].name, parameters.contents[0]).execute();
        }
        reader.readAsText(file, 'UTF-8'); // déclenchement du reader
      }
    }
  }

  /**
   * chargement des médias
   * @param imgParams 
   */
  public loadImage(imgParams: ImageUploadParameter ) {
    
    if(!imgParams.dom) {
      imgParams.dom = this.model.domainCode;
    }
    if(!imgParams.bu) {
      imgParams.bu = this.model.businessUnitCode;
    }
    if(!imgParams.vision) {
      imgParams.vision = ViewData.wildcardMainVision;
    }

    let params: UploadParameter = {
      callback: this.loadImageCallback.bind(this), // sera alimenté par le batchEngine
      accept: null,
      multiple: !!imgParams.multiple,
      elementRef: imgParams.elementRef,
      component: imgParams.component,
      dom: imgParams.dom,
      bu: imgParams.bu, 
      vision: imgParams.vision,
      viewCallback: imgParams.viewCallback,
      environment: imgParams.environment,
      thumb: imgParams.thumb
    }
    WizUploadManager.loadFile(params);
  }

  /** 
  * Callback de l'upload d'image
  * 
  * @param params 
  */
 private loadImageCallback(params: UploadParameter) {
   if (params.files?.length > 0 && params.contents?.length > 0) {
    
    // console.log('batchengine loadImageCallback', params.files);
    
    //new
    for (let index = 0; index < params.files.length; index++) {
      // const f: File = params.files[index];
      // console.log('loadImageCallback', params.files[index]);
      // on stock en webp (les contents, c'est du webP)
      const newName = params.files[index].name.replace(/\.[^/.]+$/, '') + '.webp';
      const webpFile = new File([], newName, { type: 'image/webp' });
      const media: Attachment = AttachmentService.buildAttachment(
        webpFile, 
        params.contents[index],
        params.dom, params.bu, params.vision
      );
      
      // const media: Attachment = AttachmentService.buildAttachment(
      //   params.files[index], 
      //   params.contents[index],
      //   params.dom, params.bu, params.vision
      // );
      
      media.metadatas = {};
      media.metadatas['noEncode'] = "true";
      // console.log('noEncode', media);
      // media.v2 = true;
     
     AttachmentService.getInstance().uploadAttachment(media)
       .subscribe(({ blobId }) => {
         if (blobId) {
          
          let realResult: string = blobId;
          if(!!media.metadatas?.noEncode) {
            realResult = media.fileName;
          }
          
          WizEventManager.dispatchEvent(
            params.elementRef,
            new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, params.component, realResult)
           );

           if(!!params.viewCallback) {
            params.viewCallback(realResult);
           }
         }
       });
    }

   }
 }

  /**
   * chargement des médias
   * Par défaut, image (Jpg/png)
   * @param parameters 
   */
  public loadFile(parameters: UploadParameter) {
    parameters.callback = this.loadFileCallback.bind(this); // on force le retour
    WizUploadManager.loadFile(parameters);
  }

  /**
   * retour du WizUploadManager. On contrôle les erreurs avant retour à la vue 
   * @param parameters 
   */
  public loadFileCallback(parameters: UploadParameter) {
    if (parameters.errors?.length > 0) {
      parameters.errors.forEach(err => {
        WizPopUp.toast(err, WizIcon.TOAST_ERROR).show();
      });
    } else {
      parameters.viewCallback(parameters);
    }
  }

   /** 
    * drop sur un composant de drop 
    * @param parameters 
    */
  public dropFile(parameters: UploadParameter) {
    if (parameters.files?.length > 0) {  
      WizCommandManager.setLock(true);  
      
        if(!parameters.contents) { // on initialise le tableau
          parameters.contents = [];
        }

        for (let index = 0; index < parameters.files.length; index++) {

          // on alimente le tableau de contenu pour avoir la bonne taille (retour en [i] = )
          parameters.contents[index] = null;

          let reader = new FileReader();
          reader.onload = (event: any) => {
            const resAr: string[] = (event.target.result as string).split(',');
            parameters.contents[index] = resAr[1];
            // parameters.contents.push(resAr[1]); //  = [resAr[1]];

            // on vérifie que tout est rempli
            let stillNull: boolean = false;
            parameters.contents.forEach(v => {
              if(!v) {
                stillNull = true;
              }
            });

            if(!stillNull && parameters.contents.length === parameters.files.length) { // tout est rempli, on passe à la suite
              this.dropFileCallBack(parameters);
            }

            // if(index === parameters.files.length - 1) {
            //   this.dropFileCallBack(parameters);
            // } else {
            //   // console.log('not the end on drop file')
            // }
            
          };
          reader.readAsDataURL(parameters.files[index]);
        }
        
    }
  }

  /**
   * Callback de l'upload de fichier
   * @param parameters 
   */
  public dropFileCallBack(parameters: UploadParameter): void {
    if (parameters.files?.length > 0 && parameters.contents?.length > 0) {
      let vision: VisionVO = this.model.currentVision;
      
      for (let index = 0; index < parameters.files.length; index++) {

        // préparation du attachment
        let media: Attachment = AttachmentService.buildAttachment(
          //parameters.files[index],
          new File([], parameters.files[index].name, { type: parameters.files[index].type }),
          parameters.contents[index],
          vision.domain, vision.businessUnitCode, ViewData.wildcardMainVision
        );
        
        if(!media.fileType) {
          media.fileType = 'unknown';
        }

        //if(!!parameters.noEncode) {
          media.metadatas = {};
          media.metadatas['noEncode'] = "true";
          // console.log('noEncode', media);
        // }

        // chemin remote désiré
        media.directory = parameters.targetPath;

        AttachmentService.getInstance().uploadAttachment(media)
          .subscribe(({ blobId }) => {

            let message: string;

            if (blobId) {
              WizEventManager.dispatchEvent(parameters.elementRef,new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, null, blobId));
              message = [media.fileName, WizTranslationManager.getInstance().translateKey('$.common.free.drop.success.$')].join(': ');
              WizPopUp.toast(message, WizIcon.TOAST_SUCCESS).show();       
            } else {
              message = [media.fileName, WizTranslationManager.getInstance().translateKey('$.common.free.drop.error.$')].join(': ');
              WizPopUp.toast(message, WizIcon.TOAST_ERROR).show();
            }
            
            media.blobId = blobId;

            if(!parameters.medias) {
              parameters.medias = [];
            }
            parameters.medias.push(media);


            // if(index === parameters.files.length - 1) {
            if(parameters.medias.length === parameters.files.length) { // OK, on a tout le monde
              if(!!parameters.viewCallback) {
                if(parameters.medias.length === 1) {
                  // console.log('send for one', index, parameters.files.length)
                  parameters.viewCallback(media, blobId); // vieux mode de retour, 1 seul
                } else {
                  // console.log('send for more')
                  parameters.viewCallback(parameters.medias);
                }
                
              }
              WizCommandManager.setLock(false);
            }
          });
        }
      
     }
  }


   /** 
    * drop sur un composant de drop 
    * @param parameters 
    */
   public dropFileV2(parameters: UploadParameter) {
    if (parameters.files?.length > 0) {  
      
        
      for (let index = 0; index < parameters.files.length; index++) {
        let reader = new FileReader();
        reader.onload = (event: any) => {
          const resAr: string[] = (event.target.result as string).split(',');
          if(!parameters.contents) {
            parameters.contents = [];
          }
          parameters.contents.push(resAr[1]); //  = [resAr[1]];
          if(index === parameters.files.length - 1) {
            this.dropFileCallBackV2(parameters);
          }
          
        };
        reader.readAsDataURL(parameters.files[index]);
      }
    }
  }

   /**
   * Callback de l'upload de fichier
   * @param parameters 
   */
   public dropFileCallBackV2(parameters: UploadParameter): void {
    if (parameters.files?.length > 0 && parameters.contents?.length > 0) {
      let vision: VisionVO = this.model.currentVision;
      
      for (let index = 0; index < parameters.files.length; index++) {
        const media: Attachment = AttachmentService.buildAttachment(
          parameters.files[index], 
          parameters.contents[index],
          vision.domain, vision.businessUnitCode, vision.code
        );
        
        
        if(!media.fileType) {
          media.fileType = 'unknown';
        }

        // chemin remote désiré
        media.directory = parameters.targetPath;

        let callback: Function = null;
        if(index === parameters.files.length - 1) {
            callback = parameters.viewCallback;
        } 

        new AttachmentCmd(this.model).create(media, callback).execute();
      }
    }
  }
}

